import { Temporal } from '@js-temporal/polyfill';
import { z } from 'zod';

import { Actor } from '../../events';

export const ClosedWorkflowStatus = z.object({
  status: z.enum(['completed', 'cancelled']),
  reasonId: z.string().optional(),
});
const DeprecatedResolvedWorkflowStatus = z.enum(['completed', 'cancelled']);

export const ResolvedWorkflowStatus = ClosedWorkflowStatus.or(
  DeprecatedResolvedWorkflowStatus.transform((status) => ClosedWorkflowStatus.parse({ status, reasonId: undefined })),
);
export type ResolvedWorkflowStatus = z.infer<typeof ResolvedWorkflowStatus>;

const ActiveWorkflowStatus = z.object({
  status: z.literal('active'),
});
const DeprecatedUnresolvedWorkflowStatus = z.literal('active');

export const UnresolvedWorkflowStatus = ActiveWorkflowStatus.or(
  DeprecatedUnresolvedWorkflowStatus.transform((status) => ActiveWorkflowStatus.parse({ status })),
);
export type UnresolvedWorkflowStatus = z.infer<typeof UnresolvedWorkflowStatus>;

export const WorkflowStatus = z.union([ResolvedWorkflowStatus, UnresolvedWorkflowStatus]);
export type WorkflowStatus = z.infer<typeof WorkflowStatus>;

export const isResolvedWorkflowStatus = (status: WorkflowStatus): status is ResolvedWorkflowStatus =>
  ResolvedWorkflowStatus.safeParse(status).success;

export const isUnresolvedWorkflowStatus = (status: WorkflowStatus): status is UnresolvedWorkflowStatus =>
  UnresolvedWorkflowStatus.safeParse(status).success;

interface BaseSerializedWorkflowInstanceStatus {
  timestamp: Temporal.ZonedDateTimeLike | string;
  comment: string;
  actor: Actor;
}

export interface SerializedActiveWorkflowInstanceStatus extends BaseSerializedWorkflowInstanceStatus {
  status: 'active';
}

export interface SerializedNonActiveWorkflowInstanceStatus extends BaseSerializedWorkflowInstanceStatus {
  status: ResolvedWorkflowStatus['status'];
  reasonId: string | undefined;
}

export type SerializedWorkflowInstanceStatus =
  | SerializedActiveWorkflowInstanceStatus
  | SerializedNonActiveWorkflowInstanceStatus;

export interface ActiveWorkflowInstanceStatus extends SerializedActiveWorkflowInstanceStatus {
  timestamp: Temporal.ZonedDateTime;
}

export interface NonActiveWorkflowInstanceStatus extends SerializedNonActiveWorkflowInstanceStatus {
  timestamp: Temporal.ZonedDateTime;
}

export type WorkflowInstanceStatus = ActiveWorkflowInstanceStatus | NonActiveWorkflowInstanceStatus;

export function reviveWorkflowInstanceStatus(serialized: SerializedWorkflowInstanceStatus): WorkflowInstanceStatus {
  return {
    ...serialized,
    timestamp: Temporal.ZonedDateTime.from(serialized.timestamp),
  };
}

export function areWorkflowStatusesEqual(left: WorkflowStatus, right: WorkflowStatus): boolean {
  if (left.status === 'active' || right.status === 'active') {
    return left.status === right.status;
  }
  return left.status === right.status && left.reasonId === right.reasonId;
}
